/**
 * The API client — the only place URLs and the envelope exist
 * (docs/IMPLEMENTATION-PLAN.md §5). Same-origin always; the CSRF token from
 * /auth/session rides every mutation.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.method && init.method !== 'GET' && csrfToken) headers.set('X-CSRF-Token', csrfToken);
  if (init.body && typeof init.body === 'string') headers.set('Content-Type', 'application/json');

  const res = await fetch(`/api/v1${path}`, { ...init, headers, credentials: 'same-origin' });
  const body = (await res.json().catch(() => null)) as
    | { data?: T; error?: { code: string; message: string } }
    | null;

  if (!res.ok || !body || body.error) {
    const err = body?.error;
    throw new ApiError(err?.code ?? 'SERVER_ERROR', res.status, err?.message ?? 'Something went wrong.');
  }
  return body.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, payload?: unknown) =>
    request<T>(path, { method: 'POST', body: payload === undefined ? null : JSON.stringify(payload) }),
  put: <T>(path: string, payload: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(payload) }),
  del: <T>(path: string, payload?: unknown) =>
    request<T>(path, { method: 'DELETE', body: payload === undefined ? null : JSON.stringify(payload) }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};

// --- contract shapes the screens consume (docs/API-CONTRACT.md) -------------

export interface Money {
  amount: number;
  currency: 'INR';
}
export interface Valuation {
  money: Money;
  asOf: string;
  isEstimate: boolean;
  source?: string;
}

export interface Session {
  userId: string;
  email: string;
  role: 'admin' | 'viewer';
  csrfToken: string;
}

export interface HoldingDto {
  id: string;
  /** `unmatched` marks a holder a statement named who is not on the family list. */
  member: { id: string; name: string; unmatched: boolean };
  /** `name` is display-ready; `fullName` is verbatim from the statement. */
  scheme: {
    name: string;
    fullName: string;
    isin: string | null;
    /** 'epf' is a provident-fund account, not a market instrument. */
    type: 'mf' | 'equity' | 'epf';
    /** RELIANCE, INFY — null for funds. */
    ticker: string | null;
    /** AMFI's cap/class — "Large Cap Fund", "ELSS". Null for shares. */
    category: string | null;
    /** Fund house, as AMFI names it today. Null for shares. */
    house: string | null;
  };
  /** Where the value came from and whether it needs refreshing. */
  price: { source: string | null; asOf: string | null; stale: boolean };
  folio: string;
  campaignId: string | null;
  status: string;
  units: number | null;
  invested: Money | null;
  valuation: Valuation | null;
  /** Gain against cost; null when cost is unknown, never a guessed zero. */
  gain: { money: Money; percent: number | null } | null;
  /**
   * `null` means no statement we can read has said. `[]` means the folio has no
   * nomination registered — a real and worth-knowing answer, so the two must
   * not be collapsed into one dash.
   */
  nominees: string[] | null;
  /** When a statement last confirmed this holding. */
  lastSeen: string | null;
  /** Newest transaction on this folio; the list highlights current-month movement. */
  lastTransactionAt: string | null;
  /**
   * Today's move: units × (latest price − the one before it).
   *
   * Null when the scheme has fewer than two prices on file — a holding whose
   * move is unknown must not render as a confident ₹0.
   */
  dayGain: { money: Money; percent: number | null } | null;
  /**
   * What a person counted by hand, and when (docs/API-CONTRACT.md §3.1).
   *
   * Never folded into `units` or `valuation` — the gap is the point. A
   * disagreement usually means a statement has not arrived yet.
   */
  confirmation: {
    units: number;
    at: string;
    /** confirmed − statement. Null when the statement carries no figure. */
    differsBy: number | null;
    agrees: boolean;
  } | null;
  /**
   * The instalment plan on this folio, from both directions (§3.2).
   *
   * `declared` is what the family says they set up; `detected` is the rhythm
   * the statements show. Neither overwrites the other — `mismatch` is the point.
   */
  sip: SipView | null;
  /** Optimistic-concurrency token; send it back with any mutation. */
  version: number;
}

export interface SipView {
  declared: {
    amount: Money;
    dayOfMonth: number;
    startDate: string;
    /** Null means perpetual — most SIPs run until cancelled. */
    endDate: string | null;
    cadence: 'monthly' | 'quarterly';
    at: string;
    /** False once the end date has passed: finished, not lapsed. */
    active: boolean;
  } | null;
  detected: {
    amount: Money;
    dayOfMonth: number;
    cadence: 'monthly' | 'quarterly' | 'irregular';
    lastInstalment: string;
    instalments: number;
    status: 'active' | 'stopped';
  } | null;
  /** Why the two disagree, in words. Null when they agree. Render verbatim. */
  mismatch: string | null;
}

/** How the wealth list is cut up, and how rows inside each group are ordered. */
export type GroupBy = 'type' | 'cap' | 'house';
export type SortBy = 'value' | 'name' | 'type' | 'cap' | 'house' | 'units' | 'confirmed';

export interface SipDto {
  id: string;
  member: { id: string; name: string; unmatched: boolean };
  scheme: { name: string; fullName: string; isin: string | null };
  folio: string;
  amount: Money;
  dayOfMonth: number;
  cadence: 'monthly' | 'quarterly' | 'irregular';
  firstInstalment: string;
  lastInstalment: string;
  instalments: number;
  status: 'active' | 'stopped';
  annualised: Money;
}

export interface SipsResponse {
  sips: SipDto[];
  activeCount: number;
  monthlyCommitment: Money;
}

export interface HoldingsResponse {
  sections: { class: string; subtotal: Valuation; dayGain: Money | null; holdings: HoldingDto[] }[];
  total: Valuation;
  /** Today's move across everything the current filter shows. Null when no holding has a price pair. */
  dayGain: Money | null;
  hiddenCount: number;
  /** Folios worth nothing — closed, not missing. Left out of the list by default. */
  closedCount: number;
  /** The same fund seen in two statements, counted once — reported, not hidden. */
  duplicates: { count: number; value: Money };
  /** Every member's share of the WHOLE portfolio, unaffected by the filter. */
  members: { id: string; name: string; total: Valuation; holdingCount: number }[];
}

export interface DashboardDto {
  netWorth: {
    total: Valuation;
    confirmed: Money;
    projected: Money;
    delta: { money: Money; percent: number | null; period: string } | null;
  };
  allocation: { slices: { class: string; value: Money; pct: number }[] };
  movers: { holdingId: string; name: string; member: string; percent: number; deltaPaise: number }[];
  freshness: {
    members: {
      memberId: string;
      name: string;
      /** Newest statement date covering THEIR folios — how current the evidence is. */
      confirmedTo: string | null;
      /** When money last went in. Information, never an alarm. */
      lastInvestmentAt: string | null;
      openFolios: number;
    }[];
  };
  campaigns: {
    id: string;
    name: string;
    hueSlot: number | null;
    corpusPaise: number | null;
    progress: number | null;
    holdingCount: number;
  }[];
  /** The one thing on Home that is wrong rather than merely interesting; null when nothing is. */
  alert: {
    kind: 'statements_behind';
    /** Server-owned wording — render verbatim, never re-phrase client-side. */
    message: string;
    members: { memberId: string; name: string; lastPeriodTo: string | null; daysBehind: number | null }[];
  } | null;
  /** Month-end portfolio values, oldest first — the hero sparkline. */
  trend: { asOf: string; valuePaise: number }[];
}

/** One side of the twelve-month line for a holding or the whole portfolio. */
export interface LotSplit {
  units: number;
  costPaise: number;
  valuePaise: number;
  gainPaise: number;
}

export interface TaxSummaryDto {
  shortTerm: LotSplit;
  longTerm: LotSplit;
  /** The annual s.112A exemption, stated as a fact — never applied as advice. */
  longTermExemptionPaise: number;
  maturingSoon: { holdingId: string; scheme: string; member: string; on: string; units: number; gainPaise: number }[];
  holdings: {
    holdingId: string;
    scheme: string;
    member: string;
    folio: string;
    shortTerm: LotSplit;
    longTerm: LotSplit;
    nextLongTermOn: string | null;
    /** Some units have no known cost, so the gain is understated. */
    incomplete: boolean;
  }[];
  /** Instruments the equity rules do not fit — reported, never folded in. */
  unclassified: { count: number; valuePaise: number };
  asOf: string;
}
